import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { IoArrowBackCircleSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import Swal from "sweetalert2";
import usersApi from "../../api/userApi";
import countries from "../../components/utils/countries";

// Roles for System Admin
const roles = [
  { label: "MASTER_ADMIN", value: "MASTER_ADMIN" },
  { label: "L1_ADMIN", value: "L1_ADMIN" },
  { label: "L2_ADMIN", value: "L2_ADMIN" },
];

const AddMA = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [generatedEmail, setGeneratedEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await usersApi.getAllUsers();
        setUsers(res.data);
      } catch (err) {
        console.error("Failed to load users", err);
      }
    };
    fetchUsers();
  }, []);

  // Auto-generate email
  useEffect(() => {
    if (selectedUser && selectedCountry) {
      const username = selectedUser.userName
        .toLowerCase()
        .replace(/\s+/g, "");
      const countryCode = selectedCountry.value.toLowerCase();
      const email = `${username}.${countryCode}@signbook.co`;
      setGeneratedEmail(email);
    }
  }, [selectedUser, selectedCountry]);

  const handleBack = () => {
    navigate("/admin-dashboard/user-management");
  };

  // Handle user selection
  const handleUserChange = (selected) => {
    const userData = selected?.data || null;
    setSelectedUser(userData);

    if (userData?.countryName) {
      const matchedCountry = countries.find(
        (c) =>
          c.label.toLowerCase() === userData.countryName.toLowerCase()
      );
      setSelectedCountry(matchedCountry || null);
    } else {
      setSelectedCountry(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser || !selectedCountry || !selectedRole) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please select all required fields",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const parentUserId = 0;

    try {
      await usersApi.updateUserRole(
        selectedUser.userId,
        selectedRole.value,
        parentUserId
      );

      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: `${selectedRole.value} created successfully`,
        confirmButtonColor: "#28a745",
      });

      navigate("/admin-dashboard/user-management");
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Failed to create admin",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  return (
    <div className="scrollable-container" style={{ height: "100%" }}>
      {/* Heading */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h1 className="mb-4">
          <strong>Add Admin</strong>
        </h1>

        <Button onClick={handleBack} variant="info" className="tooltip-btn">
          <IoArrowBackCircleSharp />
          <span className="tooltip-text">Go to Back</span>
        </Button>
      </div>

      {/* Form Card */}
      <div className="card shadow p-4">
        <p style={{ color: "#6c757d" }}>
          Please fill in the details below to create an admin account. Fields
          marked with <span style={{ color: "red" }}>*</span> are required.
        </p>

        {/* ✅ SINGLE USER DROPDOWN */}
        <div className="mb-3">
          <label className="form-label">
            Select User <span style={{ color: "red" }}>*</span>
          </label>
          <CreatableSelect
            options={users.map((u) => ({
              label: `${u.userName} (${u.userEmail})`,
              value: u.userId,
              data: u,
            }))}
            value={
              selectedUser
                ? {
                    label: `${selectedUser.userName} (${selectedUser.userEmail})`,
                    value: selectedUser.userId,
                    data: selectedUser,
                  }
                : null
            }
            onChange={handleUserChange}
            placeholder="Select user..."
            isClearable
          />
        </div>

        {/* Country */}
        <div className="mb-3">
          <label className="form-label">
            Country <span style={{ color: "red" }}>*</span>
          </label>
          <CreatableSelect
            options={countries}
            value={selectedCountry}
            isDisabled={!!selectedUser?.countryName}
            onChange={(selected) => setSelectedCountry(selected || null)}
            placeholder="Select country..."
            isClearable
          />
        </div>

        {/* Generated Email */}
        {generatedEmail && (
          <div className="mb-3">
            <label className="form-label">Generated Id</label>
            <input
              type="text"
              className="form-control"
              value={generatedEmail}
              readOnly
            />
          </div>
        )}

        {/* Role */}
        <div className="mb-3">
          <label className="form-label">
            Select Role <span style={{ color: "red" }}>*</span>
          </label>
          <CreatableSelect
            options={roles}
            value={selectedRole}
            onChange={(selected) => setSelectedRole(selected)}
            placeholder="Select role..."
            isClearable
          />
        </div>

        {/* Submit */}
        <div className="text-end">
          <button className="btn btn-primary px-4" onClick={handleSubmit}>
            Create Admin
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMA;